import cx from 'classnames';
import React, { useState } from 'react';

import styles from './ImportCsvModal.module.scss';

// Legacy error type supplied by AdminPanelContent (field contains combined row/column info)
export interface CsvError {
  field: string;
  message: string;
}

interface GroupedErrorItem {
  column?: string;
  message: string;
}
interface GroupedRow {
  row: number;
  items: GroupedErrorItem[];
}

function parseLegacyField(field: string): { row?: number; column?: string } {
  const trimmed = field.trim();

  // Support "Column: Row: 2, Column: VehicleType:" (backend format with extra Column prefix)
  const prefixedMatch = /^Column:\s*Row:\s*(\d+),\s*Column:\s*([^:]+):?$/i.exec(trimmed);
  if (prefixedMatch) {
    return { row: Number(prefixedMatch[1]), column: prefixedMatch[2].trim() };
  }

  // Support "Row: 2, Column: VehicleType" and "Row 2, Column: VehicleType"
  const colonMatch = /^Row:?\s*(\d+)\s*,\s*(?:Column:?\s*)?([^:]+):?$/i.exec(trimmed);
  if (colonMatch) {
    return { row: Number(colonMatch[1]), column: colonMatch[2].trim() };
  }

  // Support "Row 2, VehicleType" (simple format)
  const simpleMatch = /^Row\s+(\d+)(?:,\s*(.*))?$/i.exec(trimmed);
  if (simpleMatch) {
    return { row: Number(simpleMatch[1]), column: simpleMatch[2]?.trim() };
  }

  return { row: undefined, column: trimmed };
}

function groupErrors(errors: CsvError[]): GroupedRow[] {
  const map = new Map<number, GroupedRow>();
  errors.forEach(err => {
    const { row, column } = parseLegacyField(err.field);
    const resolvedRow = row ?? -1; // -1 groups unknown row
    const existing = map.get(resolvedRow);
    const item: GroupedErrorItem = { column, message: err.message };
    if (existing) {
      existing.items.push(item);
    } else {
      map.set(resolvedRow, { row: resolvedRow, items: [item] });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.row - b.row);
}

interface CsvErrorsGroupedProps {
  errors: CsvError[];
  initialRowLimit?: number;
}

export const CsvErrorsGrouped: React.FC<CsvErrorsGroupedProps> = ({
  errors,
  initialRowLimit = 10,
}) => {
  const grouped = groupErrors(errors);
  const [showAll, setShowAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const visible = showAll ? grouped : grouped.slice(0, initialRowLimit);
  const totalIssues = errors.length;
  const totalRows = grouped.length;

  const toggleRow = (rowId: number, isOpen: boolean) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (isOpen) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  };

  return (
    <div className={styles.errorList} aria-live="polite">
      <span className={styles.listWarning}>
        <i aria-hidden="true" className={cx('icon-warning', styles.warningIcon)} />
        <span className={styles.listWarningText}>
          {`Found ${totalIssues} issue${totalIssues === 1 ? '' : 's'} in ${totalRows} row${totalRows === 1 ? '' : 's'}`}
          {!showAll && totalRows > initialRowLimit && ` (showing first ${initialRowLimit})`}
        </span>
      </span>
      <div
        id="csv-error-rows"
        className={styles.errorScrollable}
        role="region"
        aria-label="CSV validation errors"
      >
        <ul className={styles.unorderedList}>
          {visible.map(r => (
            <li key={`row-${r.row}`} className={styles.groupedRow}>
              <details
                onToggle={e => toggleRow(r.row, (e.currentTarget as HTMLDetailsElement).open)}
              >
                <summary>
                  <span className={styles.rowHeader}>
                    {r.row >= 0 ? `Row ${r.row}` : 'Unknown Row'}
                  </span>
                  <span className={styles.issueCount}>
                    <span>{r.items.length}</span>
                    <span>
                      {' '}
                      issue
                      {r.items.length === 1 ? '' : 's'}
                    </span>
                  </span>
                  <span
                    className={styles.expandIndicator}
                    aria-live="polite"
                    aria-label="row expand state"
                  >
                    {expandedRows.has(r.row) ? '▼' : '►'}
                  </span>
                </summary>
                <ul className={styles.groupedRowIssues}>
                  {r.items.map((it, idx) => {
                    const key = `row-${r.row}-${idx}-${it.column || 'col'}`;
                    return (
                      <li key={key} className={styles.groupedIssue}>
                        <span className={styles.issueLocation}>
                          {it.column
                            ? it.column.replace(/([A-Z])/g, ' $1').trim()
                            : 'Unspecified Column'}
                        </span>
                        <span className={styles.issueMessage}>{it.message}</span>
                      </li>
                    );
                  })}
                </ul>
              </details>
            </li>
          ))}
        </ul>
      </div>
      {totalRows > initialRowLimit && (
        <button
          type="button"
          onClick={() => setShowAll(s => !s)}
          className={cx(styles.showAllButton, 'tt-btn-secondary')}
          aria-expanded={showAll}
          aria-controls="csv-error-rows"
        >
          {showAll ? 'Show Less' : `Show All Rows (${totalRows})`}
        </button>
      )}
    </div>
  );
};

export default CsvErrorsGrouped;
